import { BREAKPOINTS } from "./constants";

type ImageConfig = {
  crop?:
    | "16:9"
    | "16:10"
    | "2:1"
    | "5:1"
    | "1:1"
    | "4:3"
    | "3:2"
    | "2:3"
    | "3:4"
    | "6:7"
    | "9:16"
    | "45:64"
    | "50:19";
  width?: number;
  height?: number;
  quality?: number;
  // Skip the SVG/GIF freeze rerouting below and return the source
  // untouched — for the one context (StoryHero) that plays the animation
  // itself and provides its own pause control.
  allowAnimation?: boolean;
};
export const createImageUrl = (src: string, config?: ImageConfig): string => {
  const url = new URL(src.startsWith("http") ? src : `https:${src}`);
  if (config?.crop) url.searchParams.set("crop", config?.crop + ",smart");
  if (config?.width) url.searchParams.set("width", config.width.toString());
  if (config?.height) url.searchParams.set("height", config.height.toString());
  if (config?.quality)
    url.searchParams.set("quality", config.quality.toString());

  // CMS "main image" assets can be SVGs with their own embedded CSS
  // animation. That's fine for the one place it gets a pause control
  // (StoryHero), but everywhere else the same image is reused as a small
  // teaser/list thumbnail it would just autoplay with no way to stop it —
  // a WCAG 2.2.2 violation. Routing it through the image proxy with
  // freeze=1 strips the animation server-side so it renders as a static
  // frame instead.
  if (!config?.allowAnimation && /\.svg$/i.test(url.pathname)) {
    const params = new URLSearchParams({
      token: process.env.NEXT_PUBLIC_IMAGE_PROXY_TOKEN || "",
      freeze: "1",
      url: url.href,
    });
    return `/api/image-proxy?${params.toString()}`;
  }

  // Animated GIFs have the same problem, but unlike SVG there's no CSS
  // animation to pause — the motion is baked into the file's own frame
  // timing, which the browser gives no control over at all. Asking the
  // CDN for a static format (fm=png) collapses it to a single frame at
  // the source. Still needs the same-origin proxy detour: the global
  // image loader (imageLoader.ts) unconditionally forces fm=auto on any
  // direct CDN URL, which would otherwise undo this.
  if (!config?.allowAnimation && /\.gif$/i.test(url.pathname)) {
    url.searchParams.set("fm", "png");
    const params = new URLSearchParams({
      token: process.env.NEXT_PUBLIC_IMAGE_PROXY_TOKEN || "",
      url: url.href,
    });
    return `/api/image-proxy?${params.toString()}`;
  }

  return url.href;
};
export const createImageSizes = ({
  mobile,
  tablet,
  desktop,
}: {
  mobile: string;
  tablet: string;
  desktop: string;
}) => {
  const imageSizes = [];

  if (mobile) {
    imageSizes.push(`(max-width: ${BREAKPOINTS.sm}px) ${mobile}`);
  }

  if (tablet) {
    imageSizes.push(`(max-width: ${BREAKPOINTS.lg}px) ${tablet}`);
  }

  if (desktop) {
    imageSizes.push(desktop);
  }

  return imageSizes.join(", ");
};
